import type { SplitButtonMenuProps } from '@evisort/eds/components/SplitButton/SplitButton';
import type { Action as Eds2Action } from '@evisort/eds/types/Action';
import type { ButtonVariant } from '@evisort/eds/types/SharedButtonProps';

import { Actions as Eds2Actions } from '@evisort/eds/components/Actions/Actions';
import { SplitButton } from '@evisort/eds/components/SplitButton/SplitButton';
import { memo } from 'react';

import { toEdsIcon } from '~/common/layout/edsIconMap';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { MenuActions } from '../../types/MenuActions';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { UserAction } from '../../types/UserAction';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';

interface Props {
  /** Primary actions (buttons) */
  actions: UserAction[];
  /** CSS Flex */
  flex?: 'auto' | 'none';
  /** CSS Flex-justify */
  justify?: string;
  /** Additional actions rendered under an ActionsMenu */
  moreActions?: UserAction[];
  /** this props will control the position of moreActions menu */
  moreActionsPosition?: 'left' | 'right';
  /** Size configuration (if appropriate) */
  size?: SharedButtonProps['size'];
  /** When set to `true`, indicates the actions should wrap inside their container. */
  wrap?: boolean;
}

const LEVEL_TO_VARIANT = {
  ai: 'ai',
  cta: 'ai',
  danger: 'danger',
  primary: 'primary',
  secondary: 'secondary',
  subtle: 'tertiary',
  tertiary: 'tertiary',
} satisfies Partial<Record<NonNullable<UserAction['level']>, ButtonVariant>>;

function resolveTooltip(action: UserAction) {
  const text =
    action.disabled && action.disabledTooltip
      ? action.disabledTooltip
      : action.tooltip;

  return text ? { text } : undefined;
}

function resolveVariant(action: UserAction) {
  if (action.isDanger) {
    return 'danger';
  }

  if (!action.level) {
    return undefined;
  }

  return LEVEL_TO_VARIANT[action.level as keyof typeof LEVEL_TO_VARIANT];
}

function userActionToEds2Action(
  action: UserAction,
  { iconToolbar }: { iconToolbar: boolean },
): Eds2Action {
  const variant = resolveVariant(action);

  return {
    current: iconToolbar && action.status === 'active' ? true : undefined,
    disabled: action.disabled,
    href: action.href,
    icon: action.icon
      ? { key: toEdsIcon(action.icon), label: action.text }
      : undefined,
    isLoading: action.isLoading,
    label: action.text,
    onClick: action.onClick,
    text: action.text,
    tooltip: resolveTooltip(action),
    variant,
  };
}

function isUnmappableUserAction(action: UserAction) {
  // Safeguarding Only: SplitButton — stories only; no app callsite.
  if (action.actions?.length) {
    return true;
  }

  // Model Library / Model Add Scope cart chip.
  if (action.count !== undefined) {
    return true;
  }

  if (
    // Model Library, AgentSelector, Feedback categories.
    action.mode === 'chip' ||
    // IntakeForm panel footer via Footer → Actions.
    action.mode === 'submit' ||
    // Safeguarding Only: no Actions callsite uses area.
    action.mode === 'area'
  ) {
    return true;
  }

  // Safeguarding Only: no Actions callsite uses pathname.
  if (action.pathname) {
    return true;
  }

  if (
    // Document Details header (Panel) + FieldRow Clear.
    action.level === 'tertiary-active' ||
    action.level === 'tertiary-danger' ||
    // Safeguarding Only: no Actions callsite uses secondary-active.
    action.level === 'secondary-active'
  ) {
    return true;
  }

  // Feedback like/dislike hover.
  if (action.onMouseEnter || action.onMouseLeave) {
    return true;
  }

  // ReviewCard edit icons (success / danger).
  if (action.status && action.status !== 'active') {
    return true;
  }

  // Safeguarding Only: no Actions callsite uses iconPosition right.
  if (action.iconPosition === 'right') {
    return true;
  }

  return false;
}

function isIconToolbar(actions: UserAction[]) {
  return (
    actions.length > 0 && actions.every((action) => action.mode === 'icon')
  );
}

function hasEds1OnlyFeatures({
  actions,
  moreActions,
  moreActionsPosition,
}: Props) {
  if (moreActionsPosition === 'left') {
    return true;
  }

  const allActions = [...actions, ...(moreActions ?? [])];

  // Chip/count/submit, tertiary-active/danger, Feedback hover, ReviewCard status,
  // plus safeguard-only gaps (SplitButton, area, pathname, iconPosition right).
  if (allActions.some(isUnmappableUserAction)) {
    return true;
  }

  // Safeguarding Only: No app callsite mixes icon + labeled buttons in one
  // toolbar. 2.0 is all-icon (`mode="icon"`) or all-button — not both.
  const iconActionCount = actions.filter(
    (action) => action.mode === 'icon',
  ).length;

  if (iconActionCount > 0 && iconActionCount !== actions.length) {
    return true;
  }

  return false;
}

function resolveEds2Size(size: Props['size']) {
  if (size === 'xs' || size === 's') {
    return 's' as const;
  }

  return 'm' as const;
}

function resolveSplitButtonVariant(level: UserAction['level']) {
  if (level === 'primary' || level === 'secondary') {
    return level;
  }

  return 'secondary' as const;
}

function userActionToSplitMenuAction(action: UserAction): Eds2Action {
  return {
    disabled: action.disabled,
    href: action.href,
    icon: action.icon
      ? { key: toEdsIcon(action.icon), label: action.text }
      : undefined,
    isLoading: action.isLoading,
    label: action.text,
    onClick: action.onClick,
    text: action.text,
    tooltip: resolveTooltip(action),
    variant: action.isDanger ? 'danger' : undefined,
  };
}

function userActionsToSplitButtonMenu(
  splitActions: UserAction[],
): SplitButtonMenuProps {
  const hasGroups = splitActions.some((action) => action.actions?.length);

  if (hasGroups) {
    return {
      actions: [],
      groupedActions: splitActions.map((action) => ({
        title: action.text,
        actions: (action.actions ?? [action]).map(userActionToSplitMenuAction),
      })),
      label: 'more actions',
    };
  }

  return {
    actions: splitActions.map(userActionToSplitMenuAction),
    label: 'more actions',
  };
}

function ActionsEds2Body({
  actions,
  flex,
  justify,
  moreActions,
  size,
  wrap,
}: Props) {
  const iconToolbar = isIconToolbar(actions);
  const eds2Actions = actions.map((action) =>
    userActionToEds2Action(action, { iconToolbar }),
  );
  const eds2More = moreActions?.length
    ? {
        actions: moreActions.map((action) =>
          userActionToEds2Action(action, { iconToolbar: false }),
        ),
        icon: { key: 'more' as const, label: 'More' },
      }
    : undefined;

  const toolbar = (
    <Eds2Actions
      actions={eds2Actions}
      mode={iconToolbar ? 'icon' : undefined}
      moreActions={eds2More}
      size={resolveEds2Size(size)}
    />
  );

  if (justify || wrap || flex) {
    return (
      <Layout
        preset="buttons"
        flex={flex}
        justify={justify}
        wrap={wrap}
        wrapSpacing={wrap ? 2 : undefined}
      >
        {toolbar}
      </Layout>
    );
  }

  return toolbar;
}

/**
 * This component implements possible related user actions through a friendly simplified `actions`-based interface.  It is composed with the `Button`, `IconButton`, EDS 2.0 `SplitButton`, and `ActionsMenu` components respectively.
 *
 * TODO : deprecate `<Layout preset="buttons" />` components in favor of this component.
 */
function ActionsEds1Body({
  actions,
  flex,
  justify,
  moreActions,
  moreActionsPosition = 'right',
  size,
  wrap,
}: Props) {
  const actionsMenu = moreActions?.length ? (
    <ActionsMenu
      enablePortal
      actions={toMenuActions(moreActions)}
      name="more actions"
    />
  ) : null;

  return (
    <Layout
      preset="buttons"
      flex={flex}
      justify={justify}
      wrap={wrap}
      wrapSpacing={wrap ? 2 : undefined}
    >
      {actionsMenu && moreActionsPosition === 'left' && actionsMenu}
      {actions.map((action, i) => {
        const {
          actions: splitActions,
          mode,
          level: variant,
          text,
          id,
          tooltip,
          ...rest
        } = action;
        const isIconMode = mode === 'icon';
        const isSubmitMode = mode === 'submit';
        const ButtonComponent = isIconMode ? IconButton : Button;

        return splitActions ? (
          <SplitButton
            key={i}
            disabled={action.disabled}
            isLoading={action.isLoading}
            label={text}
            menu={userActionsToSplitButtonMenu(splitActions)}
            size={resolveEds2Size(size)}
            text={text}
            tooltip={tooltip ? { text: tooltip } : undefined}
            variant={resolveSplitButtonVariant(variant)}
            onClick={action.onClick}
          />
        ) : (
          <ButtonComponent
            {...rest}
            key={i}
            size={size}
            text={text}
            mode={mode}
            tooltip={isIconMode ? (tooltip ?? text) : tooltip}
            type={isSubmitMode ? 'submit' : undefined}
            variant={variant}
            PRIVATE_variant={variant as 'action' | 'primary' | 'secondary'}
            id={id}
          />
        );
      })}
      {actionsMenu && moreActionsPosition === 'right' && actionsMenu}
    </Layout>
  );
}

export const Actions = memo((props: Props) => {
  const useEds2 = useFlag(FlagType.EnableNewEdsActions);

  if (useEds2 && !hasEds1OnlyFeatures(props)) {
    return <ActionsEds2Body {...props} />;
  }

  return <ActionsEds1Body {...props} />;
});

const toMenuActions = (actions: UserAction[]): MenuActions<undefined> => {
  return actions.map((action) => {
    const { mode: _mode, actions: groupedActions, ...menuActionProps } = action; // unpack unused non-menu action props

    const menuAction = {
      ...menuActionProps,
      label: action.text,
      value: undefined,
    };

    if (groupedActions) {
      // @ts-expect-error -- see `MenuActions.d.ts` on the union nature of this interface, which is impractical to establish here without significantly increasing typing complexity.
      menuAction.options = toMenuActions(groupedActions);
    }
    return menuAction;
  });
};
